// Contactos → separador "Negócio fechado": clientes com pelo menos um negócio concluído,
// com pesquisa por nome, telefone, email, ID do imóvel, morada ou consultor.
import { useState, useMemo } from "react";
import { Star, Send, Mail, Lock, ChevronRight, Search } from "lucide-react";
import { ladosComMotivo } from "./FormulariosSatisfacao";
import { paleta } from "./tokens";

export const negocioFechado = (n) => ["escriturado", "vendido", "arrendado", "fechado"].includes(n?.estado) && !n?.deleted_at; // igual a Concluídos

const papelNoNegocio = (lead, n) => {
  const arr = n.tipo === "arrendamento";
  if (String(n.lead_comprador_id) === String(lead.id)) return arr ? "Arrendatário" : "Comprador";
  return arr ? "Senhorio" : "Vendedor";
};
// Cores fixas decididas pela Sónia: Comprador verde · Vendedor azul · Arrendatário cinzento · Senhorio amarelo
export const PAPEIS = {
  Comprador: { icon: "🛒", cor: "#15803d", bg: "#dcfce7" },
  Vendedor: { icon: "🏷", cor: "#1d4ed8", bg: "#dbeafe" },
  "Arrendatário": { icon: "🔑", cor: "#4b5563", bg: "#f3f4f6" },
  Senhorio: { icon: "🏠", cor: "#a16207", bg: "#fef9c3" },
};
export const EtiquetaPapel = ({ papel }) => { const p = PAPEIS[papel] || PAPEIS.Comprador;
  return <span style={{ background: p.bg, color: p.cor, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{p.icon} {papel}</span>; };
const fmtData = (d) => d ? new Date(d).toLocaleDateString("pt-PT") : "—";
const dataNeg = (n) => n.data_escritura_realizada || n.escritura_concluida_em || n.updated_at || "";

export function clientesComNegocioFechado(leads, negocios) {
  const fechados = (negocios || []).filter(negocioFechado);
  return (leads || [])
    .map(l => ({ lead: l, negocios: fechados.filter(n => String(n.lead_comprador_id) === String(l.id) || String(n.lead_vendedor_id) === String(l.id)).sort((a, b) => String(dataNeg(b)).localeCompare(String(dataNeg(a)))) }))
    .filter(x => x.negocios.length > 0)
    .sort((a, b) => String(dataNeg(b.negocios[0])).localeCompare(String(dataNeg(a.negocios[0]))));
}

const campo = { padding: "9px 12px", border: `1px solid ${paleta.cinza200}`, borderRadius: 8, fontSize: 13.5, fontFamily: "inherit", background: "#fff" };
const corte = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 };

export function ClientesFechados({ leads, negocios, formularios, consultores, propostas, userId, isLider, onAbrirProcessos, onEnviarForm, onVerResposta }) {
  const [q, setQ] = useState("");
  const [papel, setPapel] = useState("");
  const todos = useMemo(() => clientesComNegocioFechado(leads, negocios), [leads, negocios]);
  const termo = q.trim().toLowerCase();
  const lista = todos.filter(({ lead, negocios: ns }) => {
    if (papel && !ns.some(n => papelNoNegocio(lead, n) === papel)) return false;
    if (!termo) return true;
    const texto = [lead.nome, lead.nome_completo, lead.telefone, lead.email, lead.consultor_nome,
      ...ns.flatMap(n => [n.imovel_ref, n.imovel_morada, n.imovel_tipologia, n.consultor_ang_nome, n.consultor_venda_nome])].filter(Boolean).join(" ").toLowerCase();
    return texto.includes(termo) || String(lead.telefone || "").replace(/\D/g, "").includes(termo.replace(/\D/g, "") || "§");
  });
  const recorrentes = todos.filter(x => x.negocios.length > 1).length;

  // Um só estado de satisfação por cliente: respondido (média) · enviado · por enviar · bloqueado
  const satisfacao = (lead, ns) => {
    // Só os negócios em que quem está a ver representa este cliente (a líder vê todos)
    const meus = ns.filter(n => { const l = ladosComMotivo(n, { propostas, consultores, leads }).find(x => String(x.leadId) === String(lead.id)); return l?.disponivel && (isLider || l.consultorId === userId); });
    if (!meus.length) return { Icone: Lock, texto: "", titulo: "Cliente de outro consultor ou agência", acao: null, cor: paleta.cinza400, bg: paleta.navy50 };
    ns = meus;
    const fs = ns.map(n => ({ n, f: (formularios || []).find(x => x.negocio_id === n.id && String(x.lead_id) === String(lead.id)) })).filter(x => x.f);
    const resp = fs.filter(x => x.f.preenchido_em);
    if (resp.length) {
      const media = resp.reduce((s, x) => s + Number(x.f.avaliacao_geral || 0), 0) / resp.length;
      const ult = resp.sort((a, b) => String(b.f.preenchido_em).localeCompare(String(a.f.preenchido_em)))[0];
      return { Icone: Star, texto: media.toLocaleString("pt-PT", { maximumFractionDigits: 1 }), titulo: "Ver resposta", acao: () => onVerResposta && onVerResposta(ult.f, lead), cor: paleta.sucesso, bg: paleta.sucessoFundo };
    }
    const env = fs.find(x => x.f.enviado_em);
    if (env) return { Icone: Send, texto: "Enviado", titulo: `Enviado a ${fmtData(env.f.enviado_em)}. Reenviar`, acao: () => onEnviarForm && onEnviarForm(env.n, env.f), cor: paleta.navy900, bg: paleta.navy100 };
    const por = fs[0];
    return { Icone: Mail, texto: "Por enviar", titulo: "Enviar formulário", acao: () => onEnviarForm && onEnviarForm(por ? por.n : ns[0], por ? por.f : null), cor: paleta.aviso, bg: paleta.avisoFundo };
  };

  const grelha = isLider
    ? "minmax(190px,1.6fr) 150px 130px minmax(120px,1fr) 100px minmax(120px,0.9fr) 120px 28px"
    : "minmax(190px,1.6fr) 150px 130px minmax(120px,1fr) 100px 120px 28px";

  return (
    <div style={{ background: "#fff", border: `1px solid ${paleta.cinza200}`, borderRadius: 12, padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: paleta.navy900 }}>Clientes com negócio fechado</div>
        <div style={{ fontSize: 13, color: paleta.cinza600 }}>{todos.length} cliente(s){recorrentes ? ` · ${recorrentes} recorrente(s) com 2+ negócios` : ""}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ position: "relative", flex: "1 1 280px", display: "flex" }}>
          <Search size={16} aria-hidden="true" style={{ position: "absolute", left: 11, top: 11, color: paleta.cinza400 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Pesquisar nome, telefone, email, ID do imóvel, morada ou consultor"
            style={{ ...campo, flex: 1, paddingLeft: 34 }} />
        </div>
        <select value={papel} onChange={e => setPapel(e.target.value)} style={campo}>
          <option value="">Todos os papéis</option>
          {Object.keys(PAPEIS).map(p => <option key={p}>{p}</option>)}
        </select>
      </div>
      {lista.length === 0 && <div style={{ fontSize: 13.5, color: paleta.cinza400, padding: 12 }}>{todos.length ? "Nenhum cliente corresponde à pesquisa." : "Ainda não há clientes com negócio fechado."}</div>}
      {lista.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: isLider ? 1040 : 900, border: `1px solid ${paleta.cinza200}`, borderRadius: 12, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: grelha, gap: 10, alignItems: "center", background: paleta.navy900, color: "#fff", padding: "11px 14px", fontSize: 12.5, fontWeight: 600 }}>
              <span>Cliente</span><span>Papel</span><span>Negócios</span><span>Imóvel</span><span>Data</span>{isLider && <span>Consultor</span>}<span>Satisfação</span><span />
            </div>
            {lista.map(({ lead, negocios: ns }) => {
              const papeis = [...new Set(ns.map(n => papelNoNegocio(lead, n)))];
              const ult = ns[0];
              const sat = satisfacao(lead, ns);
              const nome = lead.nome_completo || lead.nome || "—";
              return (
                <div key={lead.id} className="lead-row" onClick={() => onAbrirProcessos(lead)} style={{ gridTemplateColumns: grelha, gap: 10 }}>
                  <div style={{ ...corte, fontSize: 14, fontWeight: 600, color: paleta.cinza900 }} title={nome}>{nome}</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{papeis.map(p => <EtiquetaPapel key={p} papel={p} />)}</div>
                  <div style={{ fontSize: 13.5, color: paleta.cinza900, whiteSpace: "nowrap" }}>
                    {ns.length > 1
                      ? <span className="dica" data-dica="Cliente recorrente" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600, color: paleta.aviso }}><Star size={13} aria-hidden="true" />{ns.length} negócios</span>
                      : "1 negócio"}
                  </div>
                  <div style={{ ...corte, fontSize: 13.5, color: paleta.cinza900 }} title={ult.imovel_ref || ""}>{ult.imovel_ref || "—"}</div>
                  <div style={{ fontSize: 13.5, color: paleta.cinza600, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtData(ult.data_escritura_realizada)}</div>
                  {isLider && <div style={{ ...corte, fontSize: 13.5, color: paleta.cinza600 }} title={lead.consultor_nome || ""}>{lead.consultor_nome || "—"}</div>}
                  <div onClick={e => e.stopPropagation()}>
                    <button onClick={sat.acao || undefined} disabled={!sat.acao} aria-label={sat.titulo} className="dica" data-dica={sat.titulo}
                      style={{ background: sat.bg, color: sat.cor, border: "none", borderRadius: 999, padding: "4px 10px", fontSize: 12.5, fontWeight: 600, cursor: sat.acao ? "pointer" : "default", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <sat.Icone size={13} aria-hidden="true" />{sat.texto}
                    </button>
                  </div>
                  <button onClick={e => { e.stopPropagation(); onAbrirProcessos(lead); }} aria-label={`Ver processos de ${nome}`} className="dica dica-dir" data-dica="Ver processos"
                    style={{ background: "none", border: "none", color: paleta.navy900, cursor: "pointer", padding: 2, display: "grid", placeItems: "center" }}>
                    <ChevronRight size={20} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
